import {
  lazy,
  type RefObject,
  Suspense,
  useEffect,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';

import { Icons } from '@/assets/icons/icons';
import { Button } from '@/components/ui/Button';
import { handleCopyText } from '@/utils/platform';

import { useEditorState } from '../hooks/useEditorState';
import {
  findEditorTables,
  findMarkdownTables,
  replaceEditorTable,
  replaceMarkdownTable,
  type TableData,
} from '../utils/table-data';

const DataGridModal = lazy(async () => ({
  default: (await import('./editor-table-data-grid')).DataGridModal,
}));

type TableActionPlacement = 'preview' | 'row' | 'column' | 'corner';

interface TableActionTarget {
  host: HTMLElement;
  key: string;
  placement: TableActionPlacement;
  table: HTMLElement;
  tableIndex: number;
}

interface EditorTableControlsProps {
  editable: boolean;
  markdown?: string;
  onBeforeExpand?: () => void;
  onChange?: (markdown: string) => void;
  rootRef: RefObject<HTMLElement | null>;
}

interface TableDocumentAdapter {
  findTables: (source: string) => Array<TableData | null>;
  replaceTable: (source: string, tableIndex: number, data: TableData) => string;
}

const markdownTableAdapter: TableDocumentAdapter = {
  findTables: (source) => findMarkdownTables(source).map((match) => match.data),
  replaceTable: replaceMarkdownTable,
};

const editorTableAdapter: TableDocumentAdapter = {
  findTables: (source) => findEditorTables(JSON.parse(source)),
  replaceTable: (source, tableIndex, data) =>
    JSON.stringify(replaceEditorTable(JSON.parse(source), tableIndex, data)),
};

interface EditorTableControlsContentProps extends Omit<
  EditorTableControlsProps,
  'markdown' | 'onChange'
> {
  adapter: TableDocumentAdapter;
  getSource: () => string;
  onSourceChange: (source: string) => string;
}

export function EditorTableControls({
  markdown: markdownProp,
  onChange: onChangeProp,
  ...props
}: EditorTableControlsProps) {
  const editor = useEditorState();
  const markdownStorage = (
    editor?.storage as { markdown?: { getMarkdown: () => string } } | undefined
  )?.markdown;

  let adapter: TableDocumentAdapter;
  let getSource: () => string;
  let onSourceChange: (next: string) => string;
  if (markdownProp !== undefined) {
    adapter = markdownTableAdapter;
    getSource = () => markdownProp;
    onSourceChange = (next) => {
      onChangeProp?.(next);
      return next;
    };
  } else if (editor && markdownStorage) {
    adapter = markdownTableAdapter;
    getSource = () => markdownStorage.getMarkdown();
    onSourceChange = (next) => {
      editor.commands.setContent(next);
      return markdownStorage.getMarkdown();
    };
  } else if (editor) {
    adapter = editorTableAdapter;
    getSource = () => JSON.stringify(editor.getJSON());
    onSourceChange = (next) => {
      editor.commands.setContent(JSON.parse(next));
      return JSON.stringify(editor.getJSON());
    };
  } else {
    return null;
  }

  if (
    markdownProp !== undefined &&
    adapter.findTables(getSource()).length === 0
  ) {
    return null;
  }

  return (
    <EditorTableControlsContent
      {...props}
      adapter={adapter}
      getSource={getSource}
      onSourceChange={onSourceChange}
    />
  );
}

function EditorTableControlsContent({
  adapter,
  editable,
  getSource,
  onBeforeExpand,
  onSourceChange,
  rootRef,
}: EditorTableControlsContentProps) {
  const [actionTargets, setActionTargets] = useState<TableActionTarget[]>([]);
  const [, refreshSource] = useState(0);
  const [expandedTableIndex, setExpandedTableIndex] = useState<number | null>(
    null
  );
  const [expandedDataOverride, setExpandedDataOverride] = useState<TableData>();
  const expandedDataRef = useRef<TableData | null | undefined>(undefined);
  const source = getSource();
  const latestSourceRef = useRef(source);
  const expectedSourceRef = useRef<string | null>(null);
  latestSourceRef.current = source;

  useEffect(() => {
    if (
      expandedTableIndex !== null &&
      expectedSourceRef.current !== null &&
      source !== expectedSourceRef.current
    ) {
      expectedSourceRef.current = null;
      setExpandedTableIndex(null);
    }
  }, [expandedTableIndex, source]);

  useEffect(() => {
    const root = rootRef.current;
    if (!root) return;

    let syncFrame: number | null = null;
    let disposed = false;
    const handleScrollCleanups = new Map<HTMLElement, () => void>();
    const viewportResizeObservers = new Map<HTMLElement, ResizeObserver>();
    const tableDimensions = new Map<
      number,
      { columns: number; rows: number }
    >();

    const ensureHost = (
      container: HTMLElement,
      axis: 'row' | 'column' | 'corner'
    ) => {
      let host = container.querySelector<HTMLElement>(
        `:scope > [data-editor-table-actions="${axis}"]`
      );
      if (!host) {
        host = document.createElement('div');
        host.dataset.editorTableActions = axis;
        container.prepend(host);
      }
      return host;
    };

    const ensureViewport = (wrapper: HTMLElement) => {
      wrapper.dataset.editorTableShell = 'active';
      let viewport = wrapper.querySelector<HTMLElement>(
        ':scope > [data-editor-table-viewport]'
      );
      const directTable =
        wrapper.querySelector<HTMLTableElement>(':scope > table');
      if (!viewport) {
        if (!directTable) return null;
        viewport = document.createElement('div');
        viewport.dataset.editorTableViewport = '';
        wrapper.insertBefore(viewport, directTable);
      }

      let canvas = viewport.querySelector<HTMLElement>(
        ':scope > [data-editor-table-canvas]'
      );
      if (!canvas) {
        const table =
          viewport.querySelector<HTMLTableElement>(':scope > table') ??
          directTable;
        if (!table) return null;
        canvas = document.createElement('div');
        canvas.dataset.editorTableCanvas = '';
        viewport.prepend(canvas);
        canvas.appendChild(table);
      }
      return canvas;
    };

    const cellHandleSelector = '.lb-editor-table-cell-handle-container';
    const rowHandleSelector = '.lb-editor-table-row-handle-container';
    const columnHandleSelector = '.lb-editor-table-column-handle-container';
    const tableHandleSelector = [
      cellHandleSelector,
      rowHandleSelector,
      columnHandleSelector,
    ].join(', ');

    const syncTrackedHandles = (
      wrapper: HTMLElement,
      viewport: HTMLElement
    ) => {
      const cellHandle = wrapper.querySelector<HTMLElement>(cellHandleSelector);
      const rowHandle = wrapper.querySelector<HTMLElement>(rowHandleSelector);
      const columnHandle =
        wrapper.querySelector<HTMLElement>(columnHandleSelector);
      const activeCell = wrapper.querySelector<HTMLElement>(
        '.lb-editor-table-cell-active'
      );
      const table = wrapper.querySelector<HTMLTableElement>('table');
      if (activeCell && table) {
        const wrapperRect = wrapper.getBoundingClientRect();
        const viewportRect = viewport.getBoundingClientRect();
        const cellRect = activeCell.getBoundingClientRect();
        const cellCenterX = cellRect.left + cellRect.width / 2;
        const cellCenterY = cellRect.top + cellRect.height / 2;

        if (cellHandle) {
          const canvas = viewport.querySelector<HTMLElement>(
            ':scope > [data-editor-table-canvas]'
          );
          if (canvas) {
            if (cellHandle.parentElement !== canvas) {
              canvas.appendChild(cellHandle);
            }
            const canvasRect = canvas.getBoundingClientRect();
            const handleX =
              activeCell.parentElement?.lastElementChild === activeCell
                ? cellRect.left
                : cellRect.right;
            cellHandle.style.top = `${cellCenterY - canvasRect.top}px`;
            cellHandle.style.left = `${handleX - canvasRect.left}px`;
            cellHandle.style.display =
              handleX >= viewportRect.left &&
              handleX <= viewportRect.right &&
              cellCenterY >= viewportRect.top &&
              cellCenterY <= viewportRect.bottom
                ? ''
                : 'none';
          }
        }

        if (rowHandle) {
          if (rowHandle.parentElement !== wrapper) {
            wrapper.appendChild(rowHandle);
          }
          const headerBottom =
            table.querySelector('th')?.getBoundingClientRect().bottom ??
            viewportRect.top;
          const minimumY =
            activeCell.tagName === 'TH' ? viewportRect.top : headerBottom;
          rowHandle.style.top = `${cellCenterY - wrapperRect.top}px`;
          rowHandle.style.left = '0px';
          rowHandle.style.display =
            cellCenterY >= minimumY && cellCenterY <= viewportRect.bottom
              ? ''
              : 'none';
        }

        if (columnHandle) {
          if (columnHandle.parentElement !== wrapper) {
            wrapper.appendChild(columnHandle);
          }
          columnHandle.style.top = `${viewportRect.top - wrapperRect.top}px`;
          columnHandle.style.left = `${cellCenterX - wrapperRect.left}px`;
          columnHandle.style.display =
            cellCenterX >= viewportRect.left &&
            cellCenterX <= viewportRect.right
              ? ''
              : 'none';
        }
      }

      if (!handleScrollCleanups.has(viewport)) {
        const onScroll = () => syncTrackedHandles(wrapper, viewport);
        viewport.addEventListener('scroll', onScroll, { passive: true });
        handleScrollCleanups.set(viewport, () =>
          viewport.removeEventListener('scroll', onScroll)
        );
      }
    };

    const ensureScrollingHandles = (
      wrapper: HTMLElement,
      canvas: HTMLElement
    ) => {
      const viewport = canvas.parentElement;
      if (!viewport) return;
      const scrollLeft = viewport.scrollLeft;
      wrapper
        .querySelectorAll<HTMLElement>(cellHandleSelector)
        .forEach((handle) => {
          if (handle.parentElement === canvas) return;
          const left = Number.parseFloat(handle.style.left);
          if (Number.isFinite(left)) {
            handle.style.left = `${left + scrollLeft}px`;
          }
          canvas.appendChild(handle);
        });
      syncTrackedHandles(wrapper, viewport);
    };

    const ensurePreviewHost = (wrapper: HTMLElement) => {
      wrapper.dataset.editorPreviewTable = '';
      const sibling = wrapper.nextElementSibling;
      if (
        sibling instanceof HTMLElement &&
        sibling.dataset.editorTableActions === 'preview'
      ) {
        return sibling;
      }
      const host = document.createElement('div');
      host.dataset.editorTableActions = 'preview';
      wrapper.after(host);
      return host;
    };

    const scheduleSync = () => {
      if (syncFrame !== null) return;
      syncFrame = requestAnimationFrame(() => {
        syncTargets();
        queueMicrotask(() => {
          syncFrame = null;
        });
      });
    };

    const syncTargets = () => {
      if (disposed) return;
      const next: TableActionTarget[] = [];

      root
        .querySelectorAll<HTMLElement>('.copyable-block-preview .tableWrapper')
        .forEach((wrapper, tableIndex) => {
          if (!viewportResizeObservers.has(wrapper)) {
            const resizeObserver = new ResizeObserver(scheduleSync);
            resizeObserver.observe(wrapper);
            viewportResizeObservers.set(wrapper, resizeObserver);
            scheduleSync();
          }
          const table =
            wrapper.querySelector<HTMLTableElement>(':scope > table');
          if (table) {
            const tableRect = table.getBoundingClientRect();
            const wrapperWidth = wrapper.clientWidth;
            const wrapperHeight = wrapper.clientHeight;
            wrapper.style.setProperty(
              '--editor-table-natural-width',
              `${tableRect.width}px`
            );
            wrapper.toggleAttribute(
              'data-editor-table-overflow-x',
              tableRect.width > wrapperWidth + 1
            );
            wrapper.toggleAttribute(
              'data-editor-table-overflow-y',
              tableRect.height > wrapperHeight + 1
            );
          }
          next.push({
            host: ensurePreviewHost(wrapper),
            key: `preview-${tableIndex}`,
            placement: 'preview',
            table: wrapper,
            tableIndex,
          });
        });

      root
        .querySelectorAll<HTMLElement>(
          '.tiptap[contenteditable="true"] .tableWrapper'
        )
        .forEach((activeTable, tableIndex) => {
          const activeCanvas = ensureViewport(activeTable);
          const activeViewport = activeCanvas?.parentElement;
          const table =
            activeCanvas?.querySelector<HTMLTableElement>(':scope > table');
          if (!activeCanvas || !activeViewport || !table) return;
          let resizeObserver = viewportResizeObservers.get(activeViewport);
          if (!resizeObserver) {
            resizeObserver = new ResizeObserver(scheduleSync);
            resizeObserver.observe(activeViewport);
            viewportResizeObservers.set(activeViewport, resizeObserver);
            scheduleSync();
          }
          resizeObserver.observe(table);
          const tableRect = table.getBoundingClientRect();
          const viewportWidth = activeViewport.clientWidth;
          const viewportHeight = activeViewport.clientHeight;
          const verticalScrollbarWidth =
            activeViewport.offsetWidth - viewportWidth;
          activeTable.style.setProperty(
            '--editor-table-natural-width',
            `${tableRect.width}px`
          );
          const hasHorizontalOverflow =
            tableRect.width > viewportWidth + verticalScrollbarWidth + 1;
          const hasVerticalOverflow = tableRect.height > viewportHeight + 1;
          activeTable.toggleAttribute(
            'data-editor-table-overflow-x',
            hasHorizontalOverflow
          );
          activeTable.toggleAttribute(
            'data-editor-table-overflow-y',
            hasVerticalOverflow
          );
          ensureScrollingHandles(activeTable, activeCanvas);
          (['row', 'column'] as const).forEach((axis) => {
            const extender = activeTable.querySelector<HTMLElement>(
              `.lb-editor-table-add-${axis}`
            );
            if (!extender?.querySelector(':scope > button')) return;
            const parent =
              axis === 'column' &&
              !hasHorizontalOverflow &&
              !hasVerticalOverflow
                ? activeCanvas
                : activeTable;
            if (extender.parentElement !== parent) parent.appendChild(extender);
            extender.setAttribute('data-editor-table-extender', axis);
            next.push({
              host: ensureHost(extender, axis),
              key: `active-${tableIndex}-${axis}`,
              placement: axis,
              table: activeTable,
              tableIndex,
            });
          });
          next.push({
            host: ensureHost(activeTable, 'corner'),
            key: `active-${tableIndex}-corner`,
            placement: 'corner',
            table: activeTable,
            tableIndex,
          });

          const firstRow = table.rows.item(0);
          const dimensions = {
            columns: firstRow
              ? Array.from(firstRow.cells).reduce(
                  (count, cell) => count + cell.colSpan,
                  0
                )
              : 0,
            rows: table.rows.length,
          };
          const previousDimensions = tableDimensions.get(tableIndex);
          tableDimensions.set(tableIndex, dimensions);
          const addedAxis = previousDimensions
            ? dimensions.columns > previousDimensions.columns
              ? 'column'
              : dimensions.rows > previousDimensions.rows
                ? 'row'
                : null
            : null;
          const followedAxis = activeTable.querySelector(
            '.lb-editor-table-add-row.is-dragging'
          )
            ? 'row'
            : activeTable.querySelector(
                  '.lb-editor-table-add-column.is-dragging'
                )
              ? 'column'
              : addedAxis;
          if (followedAxis) {
            requestAnimationFrame(() => {
              if (!activeViewport.isConnected) return;
              activeViewport.scrollTo({
                left:
                  followedAxis === 'column'
                    ? activeViewport.scrollWidth
                    : activeViewport.scrollLeft,
                top:
                  followedAxis === 'row'
                    ? activeViewport.scrollHeight
                    : activeViewport.scrollTop,
                behavior: 'auto',
              });
            });
          }
        });

      handleScrollCleanups.forEach((cleanup, viewport) => {
        if (root.contains(viewport)) return;
        cleanup();
        handleScrollCleanups.delete(viewport);
      });
      viewportResizeObservers.forEach((resizeObserver, element) => {
        if (root.contains(element)) return;
        resizeObserver.disconnect();
        viewportResizeObservers.delete(element);
      });

      setActionTargets((previous) =>
        previous.length === next.length &&
        previous.every(
          (target, index) =>
            target.key === next[index]?.key &&
            target.host === next[index]?.host &&
            target.table === next[index]?.table
        )
          ? previous
          : next
      );
    };

    const structureSelector = [
      '.tableWrapper',
      'table',
      'tbody',
      'colgroup',
      'col',
      'tr',
      'th',
      'td',
      '.lb-editor-table-add-row',
      '.lb-editor-table-add-column',
    ].join(', ');
    const matchesSelector = (node: Node, selector: string) =>
      node instanceof HTMLElement &&
      (node.matches(selector) || node.querySelector(selector) !== null);
    const isEditorContentNode = (node: Node) =>
      node instanceof HTMLElement &&
      !node.matches('.column-resize-handle, .ProseMirror-trailingBreak') &&
      node.querySelector('.column-resize-handle') === null;
    const observer = new MutationObserver((mutations) => {
      let contentChanged = false;
      let structureChanged = false;
      for (const mutation of mutations) {
        const target =
          mutation.target instanceof Element
            ? mutation.target
            : mutation.target.parentElement;
        const nodes = [...mutation.addedNodes, ...mutation.removedNodes];
        contentChanged ||=
          Boolean(target?.closest('th, td')) && nodes.some(isEditorContentNode);
        structureChanged ||=
          Boolean(
            target?.closest(
              '.lb-editor-table-add-row, .lb-editor-table-add-column'
            )
          ) || nodes.some((node) => matchesSelector(node, structureSelector));
      }
      if (contentChanged) refreshSource((revision) => revision + 1);
      if (structureChanged) scheduleSync();
    });
    const scheduleTableEventSync = (event: Event) => {
      const target = event.target;
      if (
        target instanceof Element &&
        target.closest('.tableWrapper') !== null
      ) {
        scheduleSync();
      }
    };
    observer.observe(root, { childList: true, subtree: true });
    root.addEventListener('pointerdown', scheduleTableEventSync, true);
    root.addEventListener('focusin', scheduleSync, true);
    syncTargets();

    return () => {
      disposed = true;
      if (syncFrame !== null) cancelAnimationFrame(syncFrame);
      observer.disconnect();
      root.removeEventListener('pointerdown', scheduleTableEventSync, true);
      root.removeEventListener('focusin', scheduleSync, true);
      handleScrollCleanups.forEach((cleanup) => cleanup());
      viewportResizeObservers.forEach((resizeObserver) =>
        resizeObserver.disconnect()
      );
      root
        .querySelectorAll<HTMLElement>('[data-editor-table-actions]')
        .forEach((host) => host.remove());
      root
        .querySelectorAll<HTMLElement>('[data-editor-table-extender]')
        .forEach((extender) =>
          extender.removeAttribute('data-editor-table-extender')
        );
      root
        .querySelectorAll<HTMLElement>('[data-editor-preview-table]')
        .forEach((wrapper) => {
          wrapper.removeAttribute('data-editor-preview-table');
          wrapper.removeAttribute('data-editor-table-overflow-x');
          wrapper.removeAttribute('data-editor-table-overflow-y');
          wrapper.style.removeProperty('--editor-table-natural-width');
        });
      root
        .querySelectorAll<HTMLElement>('[data-editor-table-viewport]')
        .forEach((viewport) => {
          const wrapper = viewport.parentElement;
          const canvas = viewport.querySelector<HTMLElement>(
            ':scope > [data-editor-table-canvas]'
          );
          const table = canvas?.querySelector(':scope > table');
          const columnActions = canvas?.querySelector(
            ':scope > .lb-editor-table-add-column'
          );
          canvas
            ?.querySelectorAll<HTMLElement>(tableHandleSelector)
            .forEach((handle) => {
              const left = Number.parseFloat(handle.style.left);
              if (Number.isFinite(left)) {
                handle.style.left = `${left - viewport.scrollLeft}px`;
              }
              wrapper?.appendChild(handle);
            });
          if (wrapper && table) wrapper.insertBefore(table, viewport);
          if (wrapper && columnActions) wrapper.appendChild(columnActions);
          viewport.remove();
          wrapper?.removeAttribute('data-editor-table-shell');
          wrapper?.removeAttribute('data-editor-table-overflow-x');
          wrapper?.removeAttribute('data-editor-table-overflow-y');
          wrapper?.style.removeProperty('--editor-table-natural-width');
        });
    };
  }, [rootRef]);

  const tables = adapter.findTables(source);
  const previewTableCount = actionTargets.filter(
    (target) => target.placement === 'preview'
  ).length;
  const editorTableCount = actionTargets.filter(
    (target) => target.placement === 'corner'
  ).length;
  const canExpand = (target: TableActionTarget) =>
    tables[target.tableIndex] !== null &&
    tables[target.tableIndex] !== undefined &&
    tables.length ===
      (target.placement === 'preview' ? previewTableCount : editorTableCount);
  const expandedData =
    expandedTableIndex === null
      ? undefined
      : (expandedDataOverride ?? tables[expandedTableIndex]);
  expandedDataRef.current = expandedData;

  const copyTable = (target: TableActionTarget) => {
    const table = target.table.querySelector<HTMLTableElement>('table');
    if (!table) return;
    const text = Array.from(table.rows, (row) =>
      Array.from(row.cells, (cell) => cell.innerText.trim()).join('\t')
    ).join('\n');
    void handleCopyText(text);
  };

  const closeExpandedTable = () => {
    expandedDataRef.current = undefined;
    expectedSourceRef.current = null;
    setExpandedDataOverride(undefined);
    setExpandedTableIndex(null);
  };

  const expandTable = (target: TableActionTarget) => {
    onBeforeExpand?.();
    const currentSource = getSource();
    const currentTables = adapter.findTables(currentSource);
    const renderedTableCount =
      target.placement === 'preview' ? previewTableCount : editorTableCount;
    const currentTable = currentTables[target.tableIndex];
    if (currentTables.length !== renderedTableCount || !currentTable) return;
    expandedDataRef.current = currentTable;
    setExpandedDataOverride(undefined);
    expectedSourceRef.current = currentSource;
    latestSourceRef.current = currentSource;
    setExpandedTableIndex(target.tableIndex);
  };

  const transformExpandedTable = (
    transform: (current: TableData) => TableData
  ) => {
    if (expandedTableIndex === null) return;
    const currentSource = latestSourceRef.current;
    if (expectedSourceRef.current !== currentSource) {
      closeExpandedTable();
      return;
    }
    const current =
      expandedDataRef.current ??
      adapter.findTables(currentSource)[expandedTableIndex];
    if (!current) return;
    const next = transform(current);
    if (next === current) return;
    const nextSource = adapter.replaceTable(
      currentSource,
      expandedTableIndex,
      next
    );
    const appliedSource = onSourceChange(nextSource);
    const appliedData = adapter.findTables(appliedSource)[expandedTableIndex];
    if (!appliedData) {
      closeExpandedTable();
      return;
    }
    expectedSourceRef.current = appliedSource;
    latestSourceRef.current = appliedSource;
    expandedDataRef.current = appliedData;
    setExpandedDataOverride(appliedData);
  };

  return (
    <>
      {actionTargets.map((target) =>
        createPortal(
          <>
            {(target.placement === 'preview' ||
              target.placement === 'corner') &&
              canExpand(target) && (
                <Button
                  aria-label="Open full data grid"
                  className="editor-table-action editor-table-action-expand"
                  data-table-expand
                  iconOnly
                  variant="outline"
                  size="sm"
                  onPointerDown={(event) => event.stopPropagation()}
                  onClick={() => expandTable(target)}
                  tooltip="Expand"
                >
                  <Icons.expand />
                </Button>
              )}
            {target.placement !== 'corner' && (
              <Button
                aria-label="Copy table"
                className="editor-table-action"
                iconOnly
                variant="outline"
                size="sm"
                onPointerDown={(event) => event.stopPropagation()}
                onClick={() => copyTable(target)}
                tooltip="Copy"
              >
                <Icons.copy />
              </Button>
            )}
          </>,
          target.host,
          target.key
        )
      )}

      {expandedTableIndex !== null && expandedData && (
        <Suspense fallback={null}>
          <DataGridModal
            data={expandedData}
            editable={editable}
            onTransformData={transformExpandedTable}
            onClose={closeExpandedTable}
          />
        </Suspense>
      )}
    </>
  );
}
